import { API_CONSTANTS, API_HEADERS } from '../constants/api-constants';
import axios from 'axios';
import { AxiosError } from 'axios';
import { getCurrentToken } from './httpService';

// Custom error interface for API errors
interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
    status?: number;
  };
  config?: {
    url?: string;
    method?: string;
  };
}

export type BatchCheckType =
  | 'ENM_AUTOCODER'
  | 'ENM_LEVEL'
  | 'SURGICAL_AUTOCODER';

export interface CreateBatchJobRequest {
  name: string;
  checkType: BatchCheckType;
}

export interface BatchJobData {
  batchJobId: number | string; // Can be number or string UUID
  totalAppointments: number;
  status: string;
}

export interface CreateBatchJobResponse {
  message: string;
  status: number;
  data: BatchJobData;
  timestamp: string;
}

export interface BatchJobModel {
  batchJobId: string;
  name: string;
  progressPercentage: number;
  completionStatus: string;
  createdAt: string | null;
  completedAt: string | null;
  status: string;
  total: number;
  processed: number;
  success: number;
  failed: number;
  skipped: number;
}

export interface BatchJobsListData {
  totalPages: number;
  totalElements: number;
  isFirst: boolean;
  isLast: boolean;
  models: BatchJobModel[];
}

export interface BatchJobsListResponse {
  message: string;
  status: number;
  data: BatchJobsListData;
  timestamp: string;
}

export const batchJobService = {
  // Create a new batch job with CSV file upload
  async createBatchJob(
    tenantId: string,
    batchName: string,
    file: File,
    checkType: BatchCheckType,
    provider?: string,
  ): Promise<CreateBatchJobResponse> {
    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Add the request JSON as a Blob with application/json type
      const requestPayload: CreateBatchJobRequest = {
        name: batchName,
        checkType,
      };
      formData.append(
        'request',
        new Blob([JSON.stringify(requestPayload)], {
          type: 'application/json',
        }),
      );

      // Add the file
      formData.append('file', file);

      const token = await getCurrentToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const params = provider
        ? { provider: provider.toUpperCase() }
        : undefined;
      const response = await axios.post<CreateBatchJobResponse>(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.BATCH_JOBS.CREATE}`,
        formData,
        {
          params,
          headers: {
            // Don't set Content-Type manually - axios will set it automatically with boundary
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            [API_HEADERS.TENANT_ID]: tenantId,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage || axiosError?.message || 'Failed to create batch job';

      const customError = new Error(errorMessage) as ApiError;
      customError.response = axiosError?.response;
      customError.config = axiosError?.config;

      throw customError;
    }
  },

  // Get list of batch jobs (paginated; matches other MAIA list APIs on rules service)
  async getBatchJobsList(
    tenantId: string,
    pageNo: number = 0,
    pageSize: number = 10,
    provider?: string,
  ): Promise<BatchJobsListResponse> {
    try {
      const token = await getCurrentToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const response = await axios.get<BatchJobsListResponse>(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.BATCH_JOBS.CREATE}`,
        {
          params: {
            pageNo,
            pageSize,
            ...(provider ? { provider: provider.toUpperCase() } : {}),
          },
          headers: {
            Authorization: `Bearer ${token}`,
            [API_HEADERS.TENANT_ID]: tenantId,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to fetch batch jobs list';

      const customError = new Error(errorMessage) as ApiError;
      customError.response = axiosError?.response;
      customError.config = axiosError?.config;

      throw customError;
    }
  },

  // Cancel a batch job in progress
  async cancelBatchJob(
    tenantId: string,
    batchJobId: string,
    provider?: string,
  ): Promise<{ message: string; status: number }> {
    try {
      const token = await getCurrentToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const response = await axios.post<{ message: string; status: number }>(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.BATCH_JOBS.CANCEL}`,
        null,
        {
          params: {
            batchJobId,
            ...(provider ? { provider: provider.toUpperCase() } : {}),
          },
          headers: {
            Authorization: `Bearer ${token}`,
            [API_HEADERS.TENANT_ID]: tenantId,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage || axiosError?.message || 'Failed to cancel batch job';

      const customError = new Error(errorMessage) as ApiError;
      customError.response = axiosError?.response;
      customError.config = axiosError?.config;

      throw customError;
    }
  },

  // Download batch job results (CSV file)
  async downloadBatchJobResults(
    tenantId: string,
    batchJobId: string,
    provider?: string,
  ): Promise<Blob> {
    try {
      const token = await getCurrentToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const response = await axios.get<Blob>(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.BATCH_JOBS.EXPORT_RESULTS}`,
        {
          params: {
            batchJobId,
            ...(provider ? { provider: provider.toUpperCase() } : {}),
          },
          headers: {
            Authorization: `Bearer ${token}`,
            [API_HEADERS.TENANT_ID]: tenantId,
          },
          responseType: 'blob', // Important for file downloads
        },
      );
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;

      // If the response is a blob (even for errors), try to parse it as JSON
      const contentType = axiosError.response?.headers['content-type'];
      if (
        axiosError.response?.data instanceof Blob &&
        typeof contentType === 'string' &&
        contentType.includes('application/json')
      ) {
        try {
          const text = await (axiosError.response.data as Blob).text();
          const errorData = JSON.parse(text);
          const errorMessage =
            errorData?.message || 'Failed to download batch job results';
          throw new Error(errorMessage);
        } catch {
          // If parsing fails, fall through to default error handling
        }
      }

      // Extract error message from response data if it's not a blob
      let apiErrorMessage: string | undefined;
      if (
        axiosError?.response?.data &&
        !(axiosError.response.data instanceof Blob) &&
        typeof axiosError.response.data === 'object' &&
        'message' in axiosError.response.data
      ) {
        apiErrorMessage = (axiosError.response.data as { message?: string })
          .message;
      }

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to download batch job results';

      const customError = new Error(errorMessage) as ApiError;
      // Type assertion for response to match ApiError interface
      customError.response = axiosError?.response
        ? {
            data:
              axiosError.response.data instanceof Blob
                ? undefined
                : (axiosError.response.data as { message?: string }),
            status: axiosError.response.status,
          }
        : undefined;
      customError.config = axiosError?.config;

      throw customError;
    }
  },

  async scheduleBatchJob(
    tenantId: string,
    batchName: string,
    file: File,
    scheduledAt: string,
    checkType: BatchCheckType,
    provider?: string,
  ): Promise<CreateBatchJobResponse> {
    try {
      const formData = new FormData();

      // Request payload
      const requestPayload: CreateBatchJobRequest = {
        name: batchName,
        checkType,
      };

      formData.append(
        'request',
        new Blob([JSON.stringify(requestPayload)], {
          type: 'application/json',
        }),
      );

      formData.append('file', file);

      // scheduledAt format: "YYYY-MM-DDTHH:mm" - backend uses timeZone for conversion
      formData.append('scheduleAt', scheduledAt);

      // User's timezone for API (e.g. Asia/Calcutta)
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      formData.append('timeZone', timeZone);

      const token = await getCurrentToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const scheduleParams = provider
        ? { provider: provider.toUpperCase() }
        : undefined;
      const response = await axios.post<CreateBatchJobResponse>(
        `${API_CONSTANTS.RULES_BASE_URL}${API_CONSTANTS.ENDPOINTS.BATCH_JOBS.SCHEDULE}`,
        formData,
        {
          params: scheduleParams,
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            [API_HEADERS.TENANT_ID]: tenantId,
          },
        },
      );

      return response.data;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const apiErrorMessage = axiosError?.response?.data?.message;

      const errorMessage =
        apiErrorMessage ||
        axiosError?.message ||
        'Failed to schedule batch job';

      const customError = new Error(errorMessage) as ApiError;
      customError.response = axiosError?.response;
      customError.config = axiosError?.config;

      throw customError;
    }
  },
};
